import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plug, Lightbulb, Droplet, Snowflake, Monitor, Plus, Radio, Trash2, Wifi, WifiOff, Clock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import BrandMark from "@/components/BrandMark";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CATEGORIES = [
  { value: "light", label: "Lighting", Icon: Lightbulb },
  { value: "water", label: "Water", Icon: Droplet },
  { value: "climate", label: "Climate", Icon: Snowflake },
  { value: "appliance", label: "Appliance", Icon: Monitor },
  { value: "electronics", label: "Electronics", Icon: Monitor },
  { value: "sensor", label: "Sensor", Icon: Radio },
  { value: "plug", label: "Smart Plug", Icon: Plug },
];

const CAT_ICON = Object.fromEntries(CATEGORIES.map((c) => [c.value, c.Icon]));

export default function Devices() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true, home: null, devices: [] });
  const [addOpen, setAddOpen] = useState(false);

  async function refresh() {
    setState((s) => ({ ...s, loading: true }));
    try {
      const { data } = await api.get("/devices");
      setState({ loading: false, home: data.home, devices: data.devices });
    } catch {
      setState({ loading: false, home: null, devices: [] });
    }
  }

  useEffect(() => { refresh(); }, []);

  async function remove(id) {
    if (!window.confirm("Remove this device?")) return;
    try {
      await api.delete(`/devices/${id}`);
      toast.success("Device removed.");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not remove device.");
    }
  }

  // If user has no home, send them to home setup
  useEffect(() => {
    if (!state.loading && !state.home) {
      // Only redirect if API confirmed there's no home (not a transient error)
      // Give user a visible path either way via the UI below.
    }
  }, [state]);

  return (
    <div className="min-h-screen bg-bone">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-bone/85 border-b border-[#E2DFD8]" data-testid="devices-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center">
            <BrandMark variant="light" size="sm" />
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm">
            <Link to="/dashboard" className="text-ink/70 hover:text-forest" data-testid="nav-to-dashboard">Dashboard</Link>
            <Link to="/devices" className="text-forest font-medium" data-testid="nav-to-devices">Devices</Link>
          </nav>
          <div className="flex items-center gap-3 text-sm">
            <span className="hidden sm:inline text-ink/70">{user?.email}</span>
            <Button variant="ghost" onClick={async () => { await logout(); nav("/"); }} className="rounded-full" data-testid="devices-logout">Sign out</Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <p className="label-eyebrow mb-2">My devices</p>
            <h1 className="font-serif text-4xl md:text-5xl font-light tracking-tighter leading-[1.02] text-forest">
              {state.home ? state.home.name : "Devices"}
            </h1>
            {state.home && <p className="mt-2 text-sm text-ink/55">{state.home.city ? `${state.home.city} · ` : ""}{state.home.country || ""}</p>}
          </div>
          {state.home && (
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-full bg-forest hover:bg-forest-hover text-bone px-6" data-testid="add-device-btn">
                  <Plus size={16} className="mr-2" /> Add device
                </Button>
              </DialogTrigger>
              <AddDeviceDialog onCreated={() => { setAddOpen(false); refresh(); }} />
            </Dialog>
          )}
        </div>

        {state.loading ? (
          <div className="surface-card p-10 text-center text-ink/55">Loading…</div>
        ) : !state.home ? (
          <EmptyBlock
            title="Set up your home first"
            body="Create a home before adding devices."
            cta={{ to: "/onboarding/home", label: "Set up my home", testId: "goto-home-setup" }}
          />
        ) : state.devices.length === 0 ? (
          <EmptyBlock
            title="No devices connected yet"
            body="Connect your first device to start monitoring your home."
            cta={{ onClick: () => setAddOpen(true), label: "Add device", testId: "empty-add-device" }}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="devices-list">
            {state.devices.map((d) => <DeviceRow key={d.id} d={d} onDelete={remove} />)}
          </div>
        )}

        <p className="mt-12 text-xs text-ink/50 leading-relaxed max-w-2xl">
          Manual device records are for testing and don't produce live telemetry. Smart-device providers (e.g., Tuya / Smart Life) will be available once the official linking flow is enabled.
        </p>
      </main>
    </div>
  );
}

function EmptyBlock({ title, body, cta }) {
  return (
    <div className="surface-card p-10 md:p-14 text-center" data-testid="devices-empty">
      <h2 className="font-serif text-3xl text-forest font-light">{title}</h2>
      <p className="mt-3 text-ink/60 max-w-md mx-auto">{body}</p>
      <div className="mt-8">
        {cta.to ? (
          <Link to={cta.to}>
            <Button className="rounded-full bg-forest hover:bg-forest-hover text-bone px-8 h-12" data-testid={cta.testId}>
              {cta.label}
            </Button>
          </Link>
        ) : (
          <Button onClick={cta.onClick} className="rounded-full bg-forest hover:bg-forest-hover text-bone px-8 h-12" data-testid={cta.testId}>
            <Plus size={16} className="mr-2" /> {cta.label}
          </Button>
        )}
      </div>
    </div>
  );
}

function DeviceRow({ d, onDelete }) {
  const Icon = CAT_ICON[d.category] || Plug;
  const online = !!d.online;
  return (
    <div className="surface-card p-5 flex items-start gap-4" data-testid={`device-row-${d.id}`}>
      <div className="w-11 h-11 rounded-full bg-forest/5 text-forest grid place-items-center shrink-0">
        <Icon size={18} strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-serif text-xl text-ink leading-tight">{d.name}</p>
        <p className="text-xs text-ink/55 mt-0.5">
          {d.category}
          {d.room ? ` · ${d.room}` : ""}
          {d.brand ? ` · ${d.brand}` : ""}
          {d.model ? ` ${d.model}` : ""}
        </p>
        <div className="mt-3 flex items-center gap-3 flex-wrap text-[11px] font-mono">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${online ? "bg-moss/15 text-forest" : "bg-bonewarm text-ink/55"}`}>
            {online ? <Wifi size={11} /> : <WifiOff size={11} />} {online ? "online" : "offline"}
          </span>
          <span className="inline-flex items-center gap-1 text-ink/55">
            <Clock size={11} /> {d.last_sync ? new Date(d.last_sync).toLocaleString() : "never synced"}
          </span>
          <span className="inline-flex items-center gap-1 text-ink/55">provider: {d.provider || "manual"}</span>
        </div>
      </div>
      <button
        onClick={() => onDelete(d.id)}
        className="text-ink/40 hover:text-terracotta p-2"
        data-testid={`device-delete-${d.id}`}
        aria-label="Remove device"
      >
        <Trash2 size={16} strokeWidth={1.5} />
      </button>
    </div>
  );
}

function AddDeviceDialog({ onCreated }) {
  const [tab, setTab] = useState("manual");
  const [form, setForm] = useState({ name: "", category: "", room: "", brand: "", model: "" });
  const [submitting, setSubmitting] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!form.name.trim() || !form.category) {
      toast.error("Device name and category are required.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/devices", {
        name: form.name.trim(),
        category: form.category,
        room: form.room.trim() || null,
        brand: form.brand.trim() || null,
        model: form.model.trim() || null,
      });
      toast.success("Device added.");
      setForm({ name: "", category: "", room: "", brand: "", model: "" });
      onCreated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not add device.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <DialogContent className="sm:max-w-lg" data-testid="add-device-dialog">
      <DialogHeader>
        <DialogTitle className="font-serif text-2xl text-forest">Add a device</DialogTitle>
      </DialogHeader>

      <div className="flex gap-2 border-b border-[#E2DFD8] mb-4">
        <button
          onClick={() => setTab("manual")}
          className={`px-3 py-2 text-sm border-b-2 -mb-px ${tab === "manual" ? "border-forest text-forest" : "border-transparent text-ink/55"}`}
          data-testid="tab-manual"
        >
          Add manually
        </button>
        <button
          onClick={() => setTab("smart")}
          className={`px-3 py-2 text-sm border-b-2 -mb-px ${tab === "smart" ? "border-forest text-forest" : "border-transparent text-ink/55"}`}
          data-testid="tab-smart"
        >
          Connect smart device
        </button>
      </div>

      {tab === "manual" ? (
        <form onSubmit={submit} className="space-y-3" data-testid="manual-device-form">
          <div>
            <label className="label-eyebrow block mb-1">Device name</label>
            <Input required placeholder="e.g. Living room lamp"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="rounded-xl bg-white border-[#E2DFD8] h-11"
              data-testid="manual-name"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-eyebrow block mb-1">Category</label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="rounded-xl bg-white border-[#E2DFD8] h-11" data-testid="manual-category">
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="label-eyebrow block mb-1">Room (optional)</label>
              <Input placeholder="e.g. Living room"
                value={form.room}
                onChange={(e) => setForm({ ...form, room: e.target.value })}
                className="rounded-xl bg-white border-[#E2DFD8] h-11"
                data-testid="manual-room"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="Brand (optional)"
              value={form.brand}
              onChange={(e) => setForm({ ...form, brand: e.target.value })}
              className="rounded-xl bg-white border-[#E2DFD8] h-11"
              data-testid="manual-brand"
            />
            <Input placeholder="Model (optional)"
              value={form.model}
              onChange={(e) => setForm({ ...form, model: e.target.value })}
              className="rounded-xl bg-white border-[#E2DFD8] h-11"
              data-testid="manual-model"
            />
          </div>
          <p className="text-xs text-ink/55 leading-relaxed">
            Manual records are useful for testing. Noa will not generate telemetry for them — live metrics appear once a real smart device is connected.
          </p>
          <Button type="submit" disabled={submitting}
            className="w-full bg-forest hover:bg-forest-hover text-bone rounded-full h-11 disabled:opacity-60"
            data-testid="manual-submit"
          >
            {submitting ? "Adding…" : "Add device"}
          </Button>
        </form>
      ) : (
        <div className="space-y-4" data-testid="smart-device-pane">
          <p className="text-sm text-ink/75 leading-relaxed">
            Smart-device connection (Tuya / Smart Life compatible) uses an official authorization flow — you'll be redirected to the provider to approve access. Credentials are never typed into a Noa form.
          </p>
          <div className="rounded-xl border border-[#E2DFD8] bg-bonewarm p-4">
            <p className="label-eyebrow mb-1">Status</p>
            <p className="font-serif text-xl text-forest">In development</p>
            <p className="text-xs text-ink/55 mt-2">The real Tuya authorization flow is not yet enabled on noatech.ae. We'll notify you as soon as it goes live. In the meantime, use "Add manually" to track test records.</p>
          </div>
          <Button disabled className="w-full rounded-full h-11 bg-forest/20 text-forest/60 cursor-not-allowed" data-testid="smart-connect-disabled">
            Connect with Smart Life (coming soon)
          </Button>
        </div>
      )}
    </DialogContent>
  );
}
